/**
 * Обёртка графиков Recharts в духе shadcn/ui charts — на токенах продукта.
 *
 * Механизм тот же, что у shadcn: конфиг серий → CSS-переменные
 * `--color-<серия>` на контейнере, марки красятся `var(--color-<серия>)`.
 * Отличие одно и принципиальное: цвет серии — ТОЛЬКО токен ADR-0009
 * (`--data-*`), а не `--chart-N` со случайной палитрой. Тултип говорит
 * по-русски: «1 отчёт», «3 отчёта», «1 349 отчётов», единица — у числа.
 *
 * Рамкой (заголовок, единица, знаменатель, таблица-двойник) остаётся
 * `components/ChartFrame`: Recharts здесь только движок марок там, где
 * HTML-полосками не нарисовать (оси времени, сгруппированные столбцы).
 */
import type { CSSProperties, ReactNode } from 'react';
import { ResponsiveContainer } from 'recharts';
import { plural, type CountMessage } from '../../lib/format';
import './chart.css';

/** Склонение единицы: три русские формы (устарело) или сообщение-слово без `#` (`{count, plural, …}`). */
export type Forms = [string, string, string] | CountMessage;

export interface ChartSeries {
  /** Подпись серии в легенде и тултипе. */
  label: string;
  /** Токен ADR-0009 — имя переменной, не значение: `--data-series-1`. */
  token: `--data-${string}`;
  /** Склонение единицы: ['отчёт', 'отчёта', 'отчётов']. */
  forms: Forms;
}

export type ChartConfig = Record<string, ChartSeries>;

const nf = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });

/** «1 отчёт», «3 отчёта», «1 349 отчётов». */
export function quantity(value: number, forms: Forms): string {
  return `${nf.format(value)} ${plural(value, forms)}`;
}

/**
 * Круглые деления оси от нуля: 0 / 500 / 1 000 / 1 500, а не 0 / 675 / 1 349.
 * Шаг — 1, 2, 2,5 или 5 × 10ⁿ; последнее деление не ниже максимума ряда.
 */
export function niceTicks(max: number, count = 4): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];
  const raw = max / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((k) => k * power).find((s) => s >= raw) ?? 10 * power;
  const ticks: number[] = [];
  for (let v = 0; v < max + step; v += step) {
    ticks.push(Math.round(v * 1000) / 1000);
    if (v >= max) break;
  }
  return ticks;
}

export function ChartContainer({
  config,
  height,
  label,
  className = '',
  children,
}: {
  config: ChartConfig;
  height: number;
  /** Подпись графика для читалки экрана. */
  label: string;
  className?: string;
  children: ReactNode;
}) {
  const vars = Object.fromEntries(
    Object.entries(config).map(([key, s]) => [`--color-${key}`, `var(${s.token})`]),
  ) as CSSProperties;
  return (
    <div className={`achart ${className}`} style={vars} role="figure" aria-label={label}>
      {/* minWidth: в первом кадре контейнер ещё 0 px — без порога Recharts
          ругается в консоль и рисует пустоту. */}
      <ResponsiveContainer width="100%" height={height} minWidth={1}>
        {children as React.ReactElement}
      </ResponsiveContainer>
    </div>
  );
}

interface TooltipItem {
  dataKey?: string | number | ((obj: unknown) => unknown);
  value?: unknown;
}

/**
 * Содержимое тултипа. Передаётся в `<Tooltip content={…}>` функцией: Recharts
 * отдаёт `active`, `payload` и `label` текущей марки.
 */
export function ChartTooltipContent({
  active,
  payload,
  label,
  config,
  title,
  note,
}: {
  active?: boolean;
  payload?: ReadonlyArray<TooltipItem>;
  label?: unknown;
  config: ChartConfig;
  title?: (label: unknown) => ReactNode;
  note?: (label: unknown) => ReactNode;
}) {
  if (!active || !payload?.length) return null;
  const extra = note?.(label);
  return (
    <div className="achart-tip">
      <div className="achart-tip__title">{title ? title(label) : String(label ?? '')}</div>
      {payload.map((item) => {
        const key = String(item.dataKey ?? '');
        const series = config[key];
        if (!series) return null;
        const value = typeof item.value === 'number' ? item.value : Number(item.value ?? 0);
        return (
          <div className="achart-tip__row" key={key}>
            <i style={{ background: `var(${series.token})` }} aria-hidden />
            <span>{series.label}</span>
            <strong>{quantity(value, series.forms)}</strong>
          </div>
        );
      })}
      {extra ? <div className="achart-tip__note">{extra}</div> : null}
    </div>
  );
}

export function ChartLegend({ config, children }: { config: ChartConfig; children?: ReactNode }) {
  return (
    <div className="achart-legend">
      {Object.entries(config).map(([key, s]) => (
        <span key={key}>
          <i style={{ background: `var(${s.token})` }} aria-hidden /> {s.label}
        </span>
      ))}
      {children}
    </div>
  );
}
